import { gql } from "@apollo/client";
import { Content } from "components/Content";
import { MEDIA_ITEM_FRAGMENT } from "fragments";
import { Page_Flexiblecontent_Blocks_FeaturedServices } from "graphql";

import Image from "next/image";
import Link from "next/link";

interface FeaturedServicesProps
  extends Page_Flexiblecontent_Blocks_FeaturedServices {
  className?: string;
}

const FeaturedServices = ({
  className,
  title,
  description,
  link,
  services,
  alignServices,
  backgroundColor,
}: FeaturedServicesProps) => {
  const isDark = backgroundColor === "dark";
  return (
    <section className={`${className} my-10 ${isDark ? "bg-black text-white" : "bg-white text-black"}`}>
      <div
        className={`${isDark ? "py-8 lg:py-10" : ""} mx-auto max-w-[1500px] px-6 md:px-10 lg:px-20 flex flex-col lg:flex-row gap-10 lg:gap-40 ${
          alignServices ? "flex-row-reverse" : "flex-row"
        } items-center`}
      >
        <div className="flex-1 flex flex-col gap-6 items-start">
          <h2
            className={`font-serif text-[1.3rem] md:text-[2.34375rem] font-semibold italic max-w-[420px]`}
          >
            <Content className="uppercase font-bold [&>span]:font-normal [&>span]:capitalize" content={title} />
          </h2>
          <Content
            content={description}
            className={`text-[1rem] md:text-[1.171875rem] leading-[1.7rem] max-w-[450px]`}
          />
          {link && (
            <Link className={`${isDark ? "bg-white text-black" : "border-black text-black"} border rounded-full px-5 py-2 inline-block hover:border-primary hover:bg-primary transition-colors duration-300`} href={link?.url || ""} target={link?.target || "_self"}>
              <span className="">{link?.title}</span>
            </Link>
          )}
        </div>
        <div className="grid grid-cols-2 gap-5 md:gap-10 flex-1">
          {services?.map((service, index) => (
            <a key={`service-${index}`} href={service?.serviceLink?.url || ""} target={service?.serviceLink?.target || "_self"}>
              <div className="relative rounded-t-full overflow-hidden w-full aspect-[3/4]">
              <Image
                className="w-full h-full object-cover"
                src={service?.thumbnail?.sourceUrl || ""}
                alt={service?.thumbnail?.altText || ""}
                width={service?.thumbnail?.mediaDetails?.width || 0}
                height={service?.thumbnail?.mediaDetails?.height || 0}
                sizes="(max-width: 768px) 100vw, 50vw"
              />
              </div>
              <span className="text-sm lg:text-md text-center block max-w-[180px] mx-auto mt-5 font-bold uppercase">{service?.serviceLink?.title} &gt;</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedServices;

FeaturedServices.fragments = {
  pageEntry: gql`
    fragment FeaturedServicesPageFragment on Page_Flexiblecontent_Blocks_FeaturedServices {
      __typename
      title
      description
      link {
        url
        title
        target
      }
      services {
        serviceLink {
          url
          title
          target
        }
        thumbnail {
          ...MediaItemFragment
        }
      }
      alignServices
      backgroundColor
    }
    ${MEDIA_ITEM_FRAGMENT}
  `,
  serviceEntry: gql`
    fragment FeaturedServicesServiceFragment on Service_Flexiblecontent_Blocks_FeaturedServices {
      __typename
      title
      description
      link {
        url
        title
        target
      }
      services {
        serviceLink {
          url
          title
          target
        }
        thumbnail {
          ...MediaItemFragment
        }
      }
      alignServices
      backgroundColor
    }
    ${MEDIA_ITEM_FRAGMENT}
  `,
};
